import React, { useRef, useState } from 'react';

import type { genderType } from 'apis/handler/auth/authSchema';
import Button from 'components/button/Button';
import FixedLoading from 'components/common/FixedLoading';
import InputContainer from 'components/input/InputContainer';
import { useWelcomeCoupon } from 'entities/coupon';
import { getStoredReferralCode } from 'entities/referral';
import { Check, Ticket } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ApiError } from 'shared/api';
import { REFERRAL_REWARDS_ENABLED } from 'shared/constants';
import { convertOccupationToRequest, convertVisaInfoToRequest, removeWhitespace, sha256 } from 'shared/lib';
import { toast } from 'sonner';
import { useInfoStore } from 'store/useInfoStore';

import { usePostCompleteProfile } from '../../api/query';
import { sanitizeEnglishName, trackSignupFieldError } from '../../lib';
import SignupConsents, { INITIAL_SIGNUP_CONSENTS, hasRequiredSignupConsents } from '../signup/SignupConsents';

export type OnboardingForm = {
  firstName: string;
  lastName: string;
  gender: string;
  birthday: string;
  countryCode: string;
  phoneNumber: string;
  nationality: string;
  occupation: string;
  visaInfo: string;
  preferredLanguageCode: string;
};

type OnboardingFinishStepProps = {
  form: OnboardingForm;
  onInputChange: (field: string, value: string) => void;
  onClickNext: () => void;
  onSkip?: () => void;
  children?: React.ReactNode;
  validateDetails?: () => boolean;
  isInline?: boolean;
};

/** 소셜 가입 후 이름·개인정보·동의를 한 화면에서 확인하고 쿠폰을 안내한다. */
const OnboardingFinishStep = ({
  form,
  onInputChange,
  onClickNext,
  onSkip,
  children,
  validateDetails,
  isInline = false,
}: OnboardingFinishStepProps) => {
  const { t } = useTranslation('auth');
  const { eligible } = useWelcomeCoupon();
  const { setInfo, setProfileCompleted } = useInfoStore();
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [consents, setConsents] = useState(INITIAL_SIGNUP_CONSENTS);
  const { mutate: completeProfile, isPending } = usePostCompleteProfile();
  const formRef = useRef<HTMLDivElement>(null);
  const errorToastId = useRef<string | number | undefined>(undefined);

  const revealError = () => {
    requestAnimationFrame(() => {
      const error = Array.from(formRef.current?.querySelectorAll('.text-error') ?? []).find((node) => node.textContent);
      error?.parentElement?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    });
  };

  const handleNameChange = (field: 'firstName' | 'lastName', value: string) => {
    setFirstNameError('');
    setLastNameError('');
    const sanitizedName = sanitizeEnglishName(value);
    if (sanitizedName === null) return;
    onInputChange(field, sanitizedName);
  };

  const handleComplete = () => {
    if (isPending) return;

    if (form.firstName.trim() === '') {
      setFirstNameError(t('signup.step1.firstNameRequired'));
      trackSignupFieldError(4, 'first_name');
      revealError();
      return;
    }
    if (form.lastName.trim() === '') {
      setLastNameError(t('signup.step1.lastNameRequired'));
      trackSignupFieldError(4, 'last_name');
      revealError();
      return;
    }
    if (validateDetails && !validateDetails()) {
      revealError();
      return;
    }
    if (!hasRequiredSignupConsents(consents)) {
      trackSignupFieldError(4, 'agreement');
      return;
    }

    if (errorToastId.current !== undefined) toast.dismiss(errorToastId.current);
    completeProfile(
      {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        genderType: form.gender as genderType,
        birth: form.birthday,
        phoneNumber: form.phoneNumber,
        countryCode: removeWhitespace(form.countryCode, 'NUM'),
        nationality: form.nationality,
        occupationType: convertOccupationToRequest(form.occupation),
        visaType: convertVisaInfoToRequest(form.visaInfo),
        preferredLanguageCode: form.preferredLanguageCode,
        marketingConsent: consents.marketing,
        // 추천 링크로 유입돼 저장된 코드가 있으면 가입(프로필 완성) 시점에 연결
        referralCode: REFERRAL_REWARDS_ENABLED ? (getStoredReferralCode() ?? undefined) : undefined,
      },
      {
        onSuccess: (data) => {
          setInfo({
            accessToken: data.accessToken,
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName ?? '',
            userId: `${data.id}`,
            memberRole: data.memberRole,
          });
          setProfileCompleted(true);

          // 소셜 가입자의 "가입 완료"는 프로필을 다 채운 지금 — 이메일 가입과 같은 sign_up 이벤트에 method 로 구분
          sha256(data.email)
            .catch(() => '')
            .then((hashedEmail) => {
              window.dataLayer?.push({
                event: 'sign_up',
                method: 'google',
                user_signup_date: data.createdAt.slice(0, 10),
                user_gender: form.gender,
                user_nationality: form.nationality,
                user_occupation: form.occupation,
                user_visa: form.visaInfo,
                user_status: 'active',
                user_id: hashedEmail,
              });
              window.dataLayer?.push({ event: 'onboarding_complete', method: 'google' });
            });

          onClickNext();
        },
        onError: (error) => {
          // 다른 기기에서 이미 온보딩을 마쳤으면 이 기기 상태만 늦은 것 — 맞추고 그대로 진행
          if (error instanceof ApiError && error.errorCode === 'PROFILE_ALREADY_COMPLETED') {
            setProfileCompleted(true);
            onClickNext();
            return;
          }
          errorToastId.current = toast.error(error instanceof ApiError ? error.message : t('onboarding.saveError'));
        },
      },
    );
  };

  return (
    <div ref={formRef} className={isInline ? 'w-full' : 'w-[calc(100vw-24px)] max-w-[680px]'}>
      <div className="px-5 pb-5 pt-7 md:px-8">
        <p className="mb-3 flex items-center gap-1.5 pr-10 text-c2 font-medium text-gray-50">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primaryLight text-primary">
            <Check size={13} />
          </span>
          {t('onboarding.accountReady')}
        </p>
        <h1 className="text-[24px] font-bold leading-tight text-[#222]">{t('onboarding.title')}</h1>
        {eligible && (
          <div className="mt-4 flex items-center gap-4 rounded-xl border border-primary/20 bg-primaryLight px-4 py-3">
            <Ticket className="shrink-0 text-primary" size={28} aria-hidden="true" />
            <div>
              <p className="text-c1 font-semibold text-primary">{t('onboarding.rewardTitle')}</p>
              <p className="mt-1 text-c2 text-gray-50">{t('onboarding.formDescription')}</p>
            </div>
          </div>
        )}
      </div>
      <div className="px-5 md:px-8">
        <p className="mb-3 text-c2 font-light text-primary">{t('onboarding.nameDescription')}</p>
        <div className="grid w-full grid-cols-1 gap-x-4 md:grid-cols-2">
          <InputContainer
            label={t('signup.step1.firstNameLabel')}
            placeholder={t('signup.step1.firstNamePlaceholder')}
            error={firstNameError}
            value={form.firstName}
            onChange={(e) => handleNameChange('firstName', e.target.value)}
          />
          <InputContainer
            label={t('signup.step1.lastNameLabel')}
            placeholder={t('signup.step1.lastNamePlaceholder')}
            error={lastNameError}
            value={form.lastName}
            onChange={(e) => handleNameChange('lastName', e.target.value)}
          />
        </div>
        {children}
        <div className="mb-5 mt-2 border-t border-gray-20 pt-4">
          <SignupConsents value={consents} onChange={setConsents} />
        </div>
      </div>
      <div
        className={`${isInline ? '' : 'sticky bottom-0 z-[1]'} border-t border-gray-20 bg-white px-5 pb-3 pt-4 md:px-8`}
      >
        <Button
          text={t(eligible ? 'onboarding.complete' : 'onboarding.title')}
          shape="SQUARE"
          onClick={handleComplete}
          disabled={isPending || !hasRequiredSignupConsents(consents)}
        />
        {onSkip && (
          <button
            type="button"
            onClick={onSkip}
            disabled={isPending}
            className="mt-1 min-h-10 w-full text-c2 text-gray-50"
          >
            {t('onboarding.later')}
          </button>
        )}
      </div>
      {isPending && <FixedLoading />}
    </div>
  );
};

export default OnboardingFinishStep;
